import React, { useEffect, useState, CSSProperties } from "react";
import {
  Popover,
  PopoverBody,
  PopoverHeader,
  PopoverContent,
  PopoverTrigger,
} from "@clarkinc/zest-design-system/ui";
import debounce from "lodash/debounce";
import { cls } from "@wss/cls";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import loaderGif from "~/common/loader.gif";
import {
  QualityRatingItem,
  QualityRatingMembership,
} from "~/common/Store/models/QualityRating";
import getLowestDisplayPrice from "~/common/Product/getLowestDisplayPrice";
import shouldDisplayQuantityDiscounts from "~/common/Product/shouldDisplayQuantityDiscounts";
import { User } from "~/common/Store/models/User";
import Ribbon from "~/common/Ribbon/primitive";

export type GoodBetterBestProps = {
  itemNumber: string;
  qualityRating: QualityRatingMembership;
  productListingView?: "list" | "grid" | undefined;
  className?: string;
  ribbonClassName?: string;
  style?: CSSProperties;
  user: User;
};
const GoodBetterBestRibbon = ({
  itemNumber,
  qualityRating,
  productListingView,
  className,
  style,
  user,
  ribbonClassName,
}: GoodBetterBestProps) => {
  const { label } = qualityRating;
  const [loadedQualityRatingGroup, setLoadedQualityRatingGroup] =
    useState(false);
  const [qualityRatingGroup, setqualityRatingGroup] = useState<
    QualityRatingItem[]
  >([]);

  const [isLargeScreen, setIsLargeScreen] = useState(true);

  useEffect(() => {
    if (!productListingView) {
      return undefined;
    }

    const handleResize = debounce(() => {
      setIsLargeScreen(window.innerWidth >= 768);
    }, 100);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [productListingView]);

  const loadQuantityRatingGroup = async () => {
    if (loadedQualityRatingGroup) {
      return;
    }

    const endpointUrl = "/api/productlisting/qualityratinggroup/";

    try {
      const result = await fetch(
        `${endpointUrl}?itemNumber=${encodeURIComponent(itemNumber)}`,
      );

      const data: QualityRatingItem[] =
        await result.json<QualityRatingItem[]>();

      setqualityRatingGroup(data);
    } catch (error) {
      setqualityRatingGroup([]);
    } finally {
      setLoadedQualityRatingGroup(true);
    }
  };

  const getPriceDisplay = (item: QualityRatingItem) => {
    const lowestPricingProduct = {
      showBelowMinimumAdvertisedPriceForLoggedInCustomers:
        item.showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers:
        item.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
      startBuy: item.startBuy,
      price: item.price,
    };

    if (
      item.price.minimumAdvertisedPriceProperties?.type?.trim() &&
      item.price.minimumAdvertisedPriceProperties.price < item.price.price
    ) {
      return <>View product for pricing details</>;
    }

    const displayPrice = getLowestDisplayPrice(
      lowestPricingProduct,
      user,
      false,
    );
    const [displayQuantityDiscounts] = shouldDisplayQuantityDiscounts(
      lowestPricingProduct,
      displayPrice,
      user,
    );

    if (displayQuantityDiscounts) {
      return (
        <div
          data-testid="price"
          className="text-2xl price font-semibold leading-none mb-0 relative text-red-500 z-20"
        >
          {formatAsCurrency.formatIfUndefined(item.price.price)}
          <span
            data-testid="each"
            className="each text-xs leading-none text-gray-800 "
          >
            /{item.unitOfMeasure}
          </span>
        </div>
      );
    }

    return (
      <div
        data-testid="price"
        className="font-semibold text-base md:text-xl text-left text-red-500"
      >
        {formatAsCurrency.formatIfUndefined(displayPrice)}
        <span
          data-testid="each"
          className="each text-xs leading-none text-gray-800"
        >
          /{item.unitOfMeasure}
        </span>
      </div>
    );
  };

  const renderQualityRatingContent = () => {
    if (!loadedQualityRatingGroup && qualityRatingGroup.length === 0) {
      return (
        <img
          src={loaderGif}
          alt="waiting"
          className={`loadingImage ${"ml-12 mr-12 p-1-1/2"}`}
        />
      );
    }

    if (loadedQualityRatingGroup && qualityRatingGroup.length === 0) {
      return (
        <p className="bg-orange-100 border-orange-200 rounded border-solid border font-semibold text-xs leading-none my-2 p-2">
          Quality Ratings are currently unavailable for this product.
        </p>
      );
    }

    return (
      <div className="flex flex-col overflow-hidden">
        {qualityRatingGroup.map((item) => (
          <div
            className="border-gray-200 border-solid border-t-0 border-r-0 border-l-0 border-b last:border-b-0 box-border flex flex-wrap items-center overflow-hidden p-2 text-left w-full"
            key={item.itemNumber}
          >
            <div style={{ flex: "0 0 auto" }} className="float-left mr-2">
              <a href={item.link}>
                <img
                  className="w-16"
                  src={item.imagePath}
                  alt={item.description}
                />
              </a>
              {item.brand && item.brandLogo ? (
                <img
                  className="block w-16"
                  src={`${item.brandLogo}`}
                  alt={item.brand}
                />
              ) : (
                ""
              )}
            </div>

            <div className="flex-1">
              <Ribbon
                className={cls(
                  "inline-block hover:no-underline text-black font-semibold text-xs leading-[13px] py-0.5 px-2",
                  item.label === "Good" && "bg-goodBrown-100",
                  item.label === "Better" && "bg-betterGray-100",
                  item.label === "Best" && "bg-bestYellow-100",
                )}
              >
                {item.label}
              </Ribbon>
              <a
                href={item.link}
                className="block font-semibold text-sm leading-none mb-2 overflow-hidden"
              >
                {item.description}
              </a>
              <p className="text-xs leading-3 my-0 mx-auto relative text-gray-800 uppercase">
                {item.itemNumber}
              </p>
              <div className="font-semibold text-base text-left text-red-500 my-2">
                {getPriceDisplay(item)}
              </div>
            </div>
            <div className="text-xs leading-snug w-full">
              {item.ratingDescription}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`compare this ${qualityRating.label} ranked product to others`}
        onClick={loadQuantityRatingGroup}
        className={`border-none bg-transparent -left-1.5 relative align-middle ${
          productListingView === "list" ? "float-left -top-px" : "-top-[7px]"
        } ${className}`}
        key={qualityRating.groupId}
      >
        <Ribbon
          data-testid="goodBetterBestRibbon"
          data-gbb-guid={qualityRating.guid}
          data-gbb-orderby="1"
          className={cls(
            "outline-offset-2 text-black font-semibold text-xs py-0.5 leading-[13px]",
            label === "Good" && "bg-goodBrown-100",
            label === "Better" && "bg-betterGray-100",
            label === "Best" && "bg-bestYellow-100",
            ribbonClassName,
            productListingView === "grid"
              ? "pl-3 pr-2 mr-1.5 -ml-2"
              : "px-2 mr-1 mb-1.5",
          )}
          style={style}
        >
          {label}
        </Ribbon>
      </PopoverTrigger>
      <PopoverContent
        placement={isLargeScreen ? "left" : "bottom"}
        className={productListingView !== undefined ? "z-[60]" : "z-30"}
        avoidCollisions={productListingView !== undefined}
      >
        <PopoverHeader>Quality Rating</PopoverHeader>
        <PopoverBody>{renderQualityRatingContent()}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default GoodBetterBestRibbon;
